import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { SettingsManageUsersRootParamList } from "../../../../types";
import React from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  MUTATION_UPGRADE_STAFF_TO_USER_ACCOUNT,
  QUERY_STAFF_USER,
} from "../../common";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import {
  UpgradeStaffMemberForm,
  UpgradeStaffMemberFormValues,
} from "../../components/UpgradeStaffMemberForm";
import {
  Button,
  Label,
  Page,
  Stack,
  Card,
  TextField,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { Colors, View } from "react-native-ui-lib";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "../../../../utilities/i18n";
import { Staff } from "@towersystems/roam-common/lib/generated-types";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";

export type SettingsUpgradeStaffToUserAccountScreenProps =
  NativeStackScreenProps<
    SettingsManageUsersRootParamList,
    "SettingsUpgradeStaffToUserAccountScreen"
  >;

const Content = ({
  navigation,
  route,
}: SettingsUpgradeStaffToUserAccountScreenProps) => {
  const { t } = useTranslation("settings");

  const { control, handleSubmit, formState, watch } =
    useFormContext<UpgradeStaffMemberFormValues>();

  const [mutationCreateStaffMember] = useMutation<{
    createStaffUserAccount: Staff;
  }>(MUTATION_UPGRADE_STAFF_TO_USER_ACCOUNT);

  const {
    isDirty,
    isLoading,
    isSubmitting,
    isValid,
    isSubmitSuccessful,
    errors,
  } = formState;

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const flashMessagesContext = useFlashMessages();
  const { fetchAndSyncEntity } = useSetupAllStartupEntities();

  const handleOnSubmit = async (data: UpgradeStaffMemberFormValues) => {
    try {
      const { staffId, emailAddress, username, newPassword } = data;
      const response = await mutationCreateStaffMember({
        variables: {
          input: {
            staffId,
            emailAddress,
            username,
            password: newPassword,
          },
        },
      });
      if (response.data?.createStaffUserAccount) {
        try {
          await fetchAndSyncEntity("staffs");
        } catch (error) {
          // dont block on this if it fails, since the mutation
          // was successful and the staff user account has been created.
          //  We will have likely already updated the local staff list with the new user account details, so the UI should be up to date regardless.
        }
        flashMessagesContext.showMessage({
          severity: "success",
          message: "Staff member successfully upgraded to user account.",
        });
        navigation.goBack();
      } else {
        throw new Error("Failed to upgrade staff member to user account.");
      }
    } catch (error) {
      console.log("Error upgrading staff member to user account:", error);
      flashMessagesContext.showMessage({
        severity: "error",
        message:
          "Error upgrading staff member to user account. Please try again.",
      });
    }
  };

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={"Upgrade to user account"}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title="Upgrade staff member to user account"
      secondaryTitle="Set up a user account for this staff member so they can log in to Roam with their own credentials."
      scrollable={true}
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title="Credentials">
          <Stack vertical>
            <View>
              <Label required label={t("form.staff_email.label")}></Label>
              <Controller
                name="emailAddress"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    validate="email"
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_email.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label required label={t("form.staff_username.label")}></Label>
              <Controller
                name="username"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_username.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label required label={t("form.staff_password.label")}></Label>
              <Controller
                name="newPassword"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    secureTextEntry
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_password.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label
                required
                label={t("form.staff_confirm_password.label")}
              ></Label>
              <Controller
                name="confirmPassword"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    secureTextEntry
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_confirm_password.placeholder")}
                  />
                )}
              />
            </View>
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};

export const SettingsUpgradeStaffToUserAccountScreen = (
  props: SettingsUpgradeStaffToUserAccountScreenProps,
) => {
  const { route, navigation } = props;
  const { staffId } = route.params;
  const [staffUser, setStaffUser] = React.useState<Staff | null>(null);
  const transitionEnded = useHasTransitionEnded(navigation);

  const [query] = useLazyQuery<{
    staff: Staff;
  }>(QUERY_STAFF_USER);

  React.useEffect(() => {
    const fetchStaffUser = async () => {
      try {
        const { data } = await query({
          variables: { id: staffId },
        });
        if (data?.staff) {
          setStaffUser(data.staff);
        } else {
          console.error("Staff user not found");
        }
      } catch (error) {
        console.error("Error fetching staff user:", error);
      }
    };

    fetchStaffUser();
  }, [staffId]);

  if (!staffUser || !transitionEnded) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  return (
    <UpgradeStaffMemberForm
      defaultFormValues={{
        staffId: staffUser.id,
        emailAddress: "",
        username: "",
        newPassword: "",
        confirmPassword: "",
      }}
    >
      <Content {...props} />
    </UpgradeStaffMemberForm>
  );
};
